/**
 * Whether the user is working anywhere inside one editable box.
 *
 * Focus is tracked on the element wrapping both the fields and their
 * Save/Cancel, not on the controls: the buttons are siblings of the field, so
 * clearing on a control's own blur would unmount the button under the pointer
 * that was about to click it.
 */

import { useState, type FocusEvent } from 'react';

export const useEditScopeFocus = () => {
  const [focused, setFocused] = useState(false);

  return {
    focused,
    focusProps: {
      onFocus: () => setFocused(true),
      onBlur: (event: FocusEvent<HTMLElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setFocused(false);
        }
      },
    },
  };
};

/**
 * Hands focus back to the box after its Save or Cancel settles it.
 *
 * Committing disables the button that was just pressed, and the browser blurs a
 * disabled element without firing anything React can hear — which would strand
 * the box in its editing state until the user happened to focus something else.
 * Putting the caret back where the user was editing keeps the focus state
 * honest and keeps tab order from collapsing to the top of the page after every
 * save.
 */
export const refocusEditScope = (fieldId: string) => {
  document.getElementById(fieldId)?.focus();
};

export default useEditScopeFocus;
